import { Link } from "react-router-dom";
import { Scroll, Mail, MapPin, Phone } from "lucide-react";
import { SocialLinks } from "@jambushrushti/components/social/SocialLinks";

const quickLinks = [
  { title: "About Us", href: "/about" },
  { title: "Research", href: "/research" },
  { title: "Library", href: "/library" },
  { title: "Guruvani", href: "/guruvani" },
  { title: "Contact Us", href: "/contact" },
];

export function Footer() {
  return (
    <footer className="bg-primary">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-6 sm:py-10 lg:py-16">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-16">
          {/* Left Column - Logo, Description & Social */}
          <div className="space-y-4 lg:space-y-6">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-foreground shadow-lg sm:h-12 sm:w-12">
                <Scroll className="h-5 w-5 text-primary sm:h-6 sm:w-6" />
              </div>
              <div className="flex flex-col">
                <span className="font-heading text-lg font-bold text-primary-foreground sm:text-xl">
                  Jambushrusti
                </span>
                <span className="font-body text-xs text-primary-foreground/70 sm:text-sm">
                  Jain Knowledge Ecosystem
                </span>
              </div>
            </Link>

            <p className="font-body max-w-sm text-sm leading-relaxed text-primary-foreground/80">
              Preserving and sharing the timeless wisdom of Jain philosophy
              through Gurudev Muni Jambuvijayaji Maharaj Saheb's teachings,
              research tools, and scholarly resources.
            </p>

            {/* Social Links - Horizontal */}
            <div className="flex items-center gap-3 pt-1 lg:pt-2">
              <SocialLinks className="[&_a:hover]:border-primary-foreground [&_a:hover]:bg-primary-foreground [&_a:hover_svg]:text-primary [&_a]:border-primary-foreground/30 [&_a]:bg-primary-foreground/20 [&_svg]:text-primary-foreground" />
            </div>
          </div>

          {/* Center Column - Quick Links */}
          <div>
            <h4 className="font-heading mb-3 text-base font-semibold uppercase tracking-wide text-primary-foreground lg:mb-6">
              Quick Links
            </h4>
            <ul className="space-y-2 lg:space-y-3">
              {quickLinks.map(link => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="font-body text-sm text-primary-foreground/80 transition-colors hover:text-primary-foreground"
                  >
                    {link.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Right Column - Address */}
          <div>
            <h4 className="font-heading mb-3 text-base font-semibold uppercase tracking-wide text-primary-foreground lg:mb-6">
              Address
            </h4>
            <div className="space-y-3 lg:space-y-5">
              {/* Location */}
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-primary-foreground/30 sm:h-10 sm:w-10">
                  <MapPin className="h-4 w-4 text-primary-foreground sm:h-5 sm:w-5" />
                </div>
                <div className="font-body text-sm leading-relaxed text-primary-foreground/80">
                  <p>Muni Jambuvijayaji Gyan Mandir,</p>
                  <p>Near Adani Shantigram Jain Temple,</p>
                  <p>Adani Shantigram Township,</p>
                  <p>Ahmedabad 382 421</p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-primary-foreground/30 sm:h-10 sm:w-10">
                  <Mail className="h-4 w-4 text-primary-foreground sm:h-5 sm:w-5" />
                </div>
                <p className="font-body text-sm text-primary-foreground/80">
                  Email:{" "}
                  <a
                    href="mailto:contact@jambushrusti.org"
                    className="transition-colors hover:text-primary-foreground"
                  >
                    contact@jambushrusti.org
                  </a>
                </p>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-primary-foreground/30 sm:h-10 sm:w-10">
                  <Phone className="h-4 w-4 text-primary-foreground sm:h-5 sm:w-5" />
                </div>
                <div className="font-body space-y-1 text-sm text-primary-foreground/80">
                  <p>
                    Contact:{" "}
                    <a
                      href="tel:+919825044911"
                      className="transition-colors hover:text-primary-foreground"
                    >
                      +91-98250 44911
                    </a>
                  </p>
                  <p>Office: Mon - Sat: 10:00 AM - 6:00 PM</p>
                  <p>Visiting: Mon - Sat: 10:45 AM - 5:15 PM</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/20">
        <div className="container mx-auto px-4 py-3 sm:py-5">
          <div className="flex flex-col items-center justify-between gap-2 sm:flex-row sm:gap-4">
            <div className="flex items-center gap-4 text-sm">
              <Link
                to="/privacy"
                className="font-body text-primary-foreground/70 transition-colors hover:text-primary-foreground"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="font-body text-primary-foreground/70 transition-colors hover:text-primary-foreground"
              >
                Terms and Conditions
              </Link>
            </div>
            <p className="font-body text-center text-sm text-primary-foreground/70 sm:text-right">
              Copyright © {new Date().getFullYear()} Jambushrusti
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
